import React, { useState } from 'react';
import Modal from '../shared/Modal';
import { formatCurrency, formatStatus } from '../../utils/formatters';
import {
  PaymentRecord,
  PaymentStatusUpdateResponse,
  updatePaymentStatus,
} from '../../services/paymentService';
import { authService } from '../../services/auth';

// ============================================================================
// Manual payment status correction (per ADMIN_PAYMENT_STATUS_UPDATE_FE_GUIDE)
// Only manual (cash/check/card_terminal) payments on confirmed/checked_in
// reservations are correctable. Allowed transitions:
//   pending -> completed (Mark as Paid), pending -> failed (Write Off),
//   failed -> pending (Reopen), failed -> completed (Mark as Paid)
// ============================================================================

export type PaymentAction = 'mark_paid' | 'write_off' | 'reopen';

export const actionToNewStatus = (action: PaymentAction): 'pending' | 'completed' | 'failed' => {
  switch (action) {
    case 'mark_paid':
      return 'completed';
    case 'write_off':
      return 'failed';
    case 'reopen':
      return 'pending';
  }
};

export const actionLabel = (action: PaymentAction): string => {
  switch (action) {
    case 'mark_paid':
      return 'Record Payment';
    case 'write_off':
      return 'Write Off';
    case 'reopen':
      return 'Reopen';
  }
};

const actionTooltip = (action: PaymentAction): string => {
  switch (action) {
    case 'mark_paid':
      return 'Record that the customer has paid. Sets payment status to completed.';
    case 'write_off':
      return 'Customer will not pay (no-show, refused to pay). Closes the payment as uncollected.';
    case 'reopen':
      return 'Undo a write-off and put the payment back into pending state.';
  }
};

/**
 * Which correction actions apply to a payment, given its reservation's status.
 * Returns an empty list with an optional explanatory message when none apply.
 * Non-manual (gateway) payments get no actions and no message — their status
 * is driven by gateway events and correction UI would only confuse.
 */
export const getPaymentActions = (
  payment: PaymentRecord,
  reservationStatus: string
): { actions: PaymentAction[]; message?: string } => {
  if (payment.gateway_provider !== 'manual') {
    return { actions: [] };
  }
  if (reservationStatus !== 'confirmed' && reservationStatus !== 'checked_in') {
    return {
      actions: [],
      message: `Status changes are only available for confirmed or checked-in reservations (this one is ${formatStatus(reservationStatus).toLowerCase()}).`,
    };
  }
  switch (payment.status) {
    case 'pending':
      return { actions: ['mark_paid', 'write_off'] };
    case 'failed':
      return { actions: ['mark_paid', 'reopen'] };
    case 'completed':
      return { actions: [], message: 'Already paid — use the refund flow to reverse.' };
    case 'refunded':
      return { actions: [], message: 'Refunded — status correction unavailable.' };
    case 'processing':
      return { actions: [], message: 'Status will resolve via the payment gateway.' };
    default:
      return { actions: [] };
  }
};

// ============================================================================
// Action buttons (rendered per payment row)
// ============================================================================

interface PaymentStatusActionButtonsProps {
  payment: PaymentRecord;
  reservationStatus: string;
  disabled?: boolean;
  onAction: (payment: PaymentRecord, action: PaymentAction) => void;
  /** Hide the explanatory message for non-actionable manual payments (dense layouts). */
  hideMessage?: boolean;
}

export const PaymentStatusActionButtons: React.FC<PaymentStatusActionButtonsProps> = ({
  payment,
  reservationStatus,
  disabled = false,
  onAction,
  hideMessage = false,
}) => {
  const { actions, message } = getPaymentActions(payment, reservationStatus);

  if (actions.length === 0) {
    if (!message || hideMessage) return null;
    return <span className="payment-status-actions__message">{message}</span>;
  }

  return (
    <span className="payment-status-actions">
      {actions.map((action) => (
        <button
          key={action}
          type="button"
          className={`btn btn-sm ${action === 'write_off' ? 'btn-danger' : 'btn-primary'}`}
          title={actionTooltip(action)}
          disabled={disabled}
          onClick={() => onAction(payment, action)}
        >
          {actionLabel(action)}
        </button>
      ))}
      <style>{`
        .payment-status-actions {
          display: inline-flex;
          gap: var(--spacing-sm, 0.5rem);
        }
        .payment-status-actions .btn-sm {
          font-size: 0.78rem;
          padding: 0.25rem 0.6rem;
        }
        .payment-status-actions__message {
          font-size: 0.78rem;
          color: var(--text-secondary);
          font-style: italic;
        }
      `}</style>
    </span>
  );
};

// ============================================================================
// Confirmation modal (transition + required notes)
// ============================================================================

interface PaymentStatusConfirmModalProps {
  payment: PaymentRecord;
  action: PaymentAction;
  onClose: () => void;
  onUpdated: (response: PaymentStatusUpdateResponse) => void;
}

export const PaymentStatusConfirmModal: React.FC<PaymentStatusConfirmModalProps> = ({
  payment,
  action,
  onClose,
  onUpdated,
}) => {
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const newStatus = actionToNewStatus(action);
  const verb = actionLabel(action);

  const handleConfirm = async () => {
    if (!notes.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await updatePaymentStatus(
        payment.id,
        { new_status: newStatus, notes: notes.trim() },
        authService.getStoreId() ?? payment.store_id
      );
      onUpdated(response);
    } catch (err: any) {
      setError(err.message || 'Failed to update payment status');
      setSubmitting(false);
    }
  };

  const footer = (
    <>
      <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
        Cancel
      </button>
      <button
        className={`btn ${action === 'write_off' ? 'btn-danger' : 'btn-primary'}`}
        onClick={handleConfirm}
        disabled={submitting || !notes.trim()}
      >
        {submitting ? 'Submitting...' : `Confirm ${verb}`}
      </button>
    </>
  );

  return (
    <Modal isOpen={true} onClose={onClose} title={`${verb}?`} size="small" footer={footer}>
      <div className="payment-status-confirm">
        <p className="payment-status-confirm__summary">
          {payment.payment_method ? formatStatus(payment.payment_method) : 'Manual'} payment of{' '}
          <strong>{formatCurrency(payment.amount)}</strong>
        </p>
        <p className="payment-status-confirm__transition">
          Status: <strong>{formatStatus(payment.status)}</strong> {'→'}{' '}
          <strong>{formatStatus(newStatus)}</strong>
        </p>

        <div className="form-group">
          <label>
            Notes <span className="required">*</span>
          </label>
          <textarea
            className="form-textarea"
            placeholder="Why is this status changing? (required)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            rows={3}
            disabled={submitting}
            autoFocus
          />
          <div className="payment-status-confirm__count">{notes.length} / 500</div>
        </div>

        {error && <div className="error-message">{error}</div>}
      </div>

      <style>{`
        .payment-status-confirm__summary {
          margin: 0 0 var(--spacing-sm) 0;
        }
        .payment-status-confirm__transition {
          margin: 0 0 var(--spacing-md) 0;
        }
        .payment-status-confirm .form-textarea {
          width: 100%;
          resize: vertical;
        }
        .payment-status-confirm__count {
          text-align: right;
          font-size: 0.75rem;
          color: var(--text-secondary);
          margin-top: 2px;
        }
        .payment-status-confirm .error-message {
          margin-top: var(--spacing-sm);
        }
      `}</style>
    </Modal>
  );
};

/**
 * One-line result banner text for a completed update, distinguishing
 * real updates from no-ops (guide §4.6).
 */
export const statusUpdateResultText = (response: PaymentStatusUpdateResponse): string => {
  if (response.no_op) {
    return response.message || `Status was already '${response.new_status}' — no change made.`;
  }
  return `Payment updated: ${formatStatus(response.old_status)} → ${formatStatus(response.new_status)}.`;
};
